import React, { useState, useEffect } from 'react';

const API = `${process.env.REACT_APP_API_URL}`;

const AVATAR_COLORS = [
  { bg: '#ece7fb', color: '#5a3fd6' },
  { bg: '#fbe9dd', color: '#d9772c' },
  { bg: '#dcf3e3', color: '#1f9d55' },
  { bg: '#dde7fb', color: '#3b5cc4' },
  { bg: '#fbe1e6', color: '#d43f66' },
  { bg: '#e7e7ea', color: '#4b4d57' },
];

function getAvatarStyle(str) {
  let hash = 0;
  for (let c of (str || '')) hash += c.charCodeAt(0);
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
}

function MobileProviderHistory({ user }) {
  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all');
  const [selectedPatient, setSelectedPatient] = useState(null);

  const fetchUploads = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/Provider/my-uploads/${user.id}`);
      const data = await res.json();
      setUploads(data);
    } catch { }
    setLoading(false);
  };

  useEffect(() => {
    fetchUploads();
  }, [user.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Group uploads by patient UID
  const getGroupedPatients = () => {
    const map = {};
    const filtered = filter === 'all' ? uploads : uploads.filter(u => u.status === filter);
    filtered.forEach(u => {
      if (!map[u.patientUid]) {
        map[u.patientUid] = { patientUid: u.patientUid, uploads: [], lastUpload: u.uploadedAt };
      }
      map[u.patientUid].uploads.push(u);
    });
    return Object.values(map);
  };

  // Patient detail view
  if (selectedPatient) return (
    <div className="pb-6" style={{ fontFamily: 'Inter, sans-serif' }}>
      <div className="flex items-center gap-3 px-4 py-3 bg-white border-b border-gray-100">
        <button onClick={() => setSelectedPatient(null)} className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-gray-100">
          <span className="material-symbols-outlined text-gray-500">arrow_back</span>
        </button>
        <div>
          <p className="font-black text-[#141a3d] text-base">{selectedPatient.patientUid}</p>
          <p className="text-xs text-gray-400">{selectedPatient.uploads.length} report(s)</p>
        </div>
      </div>

      <div className="px-4 pt-3 space-y-3">
        {selectedPatient.uploads.map((u, i) => (
          <div key={i} className="bg-white rounded-2xl shadow-sm p-4 border border-gray-100">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: '#ece7fb' }}>
                <span className="material-symbols-outlined" style={{ fontSize: '20px', color: '#5a3fd6', fontVariationSettings: '"FILL" 1' }}>picture_as_pdf</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-[#141a3d] text-sm truncate">{u.fileName}</p>
                <p className="text-xs text-gray-400">{u.reportType} • {u.hospitalName}</p>
                <p className="text-xs text-gray-400">{new Date(u.uploadedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
              </div>
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0 ${
                u.status === 'accepted' ? 'bg-green-100 text-green-700' :
                u.status === 'rejected' ? 'bg-red-100 text-red-700' :
                'bg-amber-100 text-amber-700'
              }`}>{u.status.toUpperCase()}</span>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => window.open(`${API}/uploads/${u.filePath?.split('/').pop()}`, '_blank')}
                className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-600 font-bold text-xs flex items-center justify-center gap-1 active:scale-95">
                <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>open_in_new</span>
                View
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const grouped = getGroupedPatients();

  return (
    <div className="px-4 py-3 space-y-4" style={{ fontFamily: 'Inter, sans-serif' }}>
      <div>
        <h1 className="text-2xl font-black text-[#141a3d]">Upload History</h1>
        <p className="text-sm text-gray-500 mt-0.5">Tap a patient to view uploaded reports</p>
        <div className="w-10 h-1 rounded-full mt-2" style={{ background: '#3446e0' }}></div>
      </div>

      {/* Filter Pills */}
      <div className="flex gap-2 overflow-x-auto pb-1">
        {[
          { id: 'all', label: 'All' },
          { id: 'pending', label: 'Pending' },
          { id: 'accepted', label: 'Accepted' },
          { id: 'rejected', label: 'Rejected' },
        ].map(f => (
          <button key={f.id} onClick={() => setFilter(f.id)}
            className="px-4 py-1.5 rounded-full text-xs font-bold shrink-0 transition-all active:scale-95"
            style={filter === f.id
              ? { background: '#3446e0', color: '#ffffff' }
              : { background: '#ffffff', color: '#6b7280', border: '1px solid #e5e7eb' }}>
            {f.label}
          </button>
        ))}
      </div>

      {loading && <p className="text-center text-gray-400 text-sm py-8">Loading...</p>}

      {!loading && grouped.length === 0 && (
        <div className="bg-white rounded-2xl p-8 text-center shadow-sm border border-gray-100">
          <span className="material-symbols-outlined text-gray-200 block mb-2" style={{ fontSize: '48px' }}>history</span>
          <p className="font-bold text-gray-400">No uploads found</p>
        </div>
      )}

      <div className="space-y-3">
        {grouped.map((p, i) => {
          const style = getAvatarStyle(p.patientUid);
          return (
            <button key={i} onClick={() => setSelectedPatient(p)}
              className="w-full bg-white rounded-2xl shadow-sm p-4 flex items-center gap-3 text-left active:scale-[0.99] transition-all border border-gray-100">
              <div className="w-12 h-12 rounded-full flex items-center justify-center font-black text-base shrink-0"
                style={{ background: style.bg, color: style.color }}>
                {p.patientUid?.slice(3, 5) || 'P'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-extrabold text-[#141a3d] text-base truncate">{p.patientUid}</p>
                <p className="text-sm text-gray-400">{p.uploads.length} report(s) uploaded</p>
                <p className="text-xs text-gray-300">{new Date(p.lastUpload).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
              </div>
              <span className="material-symbols-outlined text-gray-300 shrink-0">chevron_right</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default MobileProviderHistory;